"use client"

import React, { useRef, useState } from "react"
import { Upload, X } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/shadcn/components/ui/avatar"
import { Button } from "@/shadcn/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/components/ui/dialog"
import { cn } from "@/lib/utils"

import { MD_BUTTON } from "./styles"

type AvatarDialogState = "default" | "drag-over" | "file-selected"

export default function AvatarDialog({
  avatar,
  trigger,
  onSaveAvatar,
  resetAvatar,
}: {
  avatar: string
  trigger: React.ReactNode
  onSaveAvatar: Function
  resetAvatar: Function
}) {
  const [state, setState] = useState<AvatarDialogState>("default")
  const [open, setOpen] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  function handleFiles(fileList: FileList | null) {
    const next = fileList?.[0] ?? null
    setFile(next)
    setState(next ? "file-selected" : "default")
  }

  function saveChanges() {
    if (file) {
      onSaveAvatar(file)
      setOpen(false)
    }
  }

  function reset() {
    setFile(null)
    setState("default")
    if (inputRef.current) inputRef.current.value = ""
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="tw:max-w-[425px] tw:gap-0 tw:rounded-xl tw:p-0">
        <DialogHeader className="tw:flex tw:flex-col tw:gap-2 tw:p-4">
          <DialogTitle className="tw:text-base tw:font-medium tw:leading-none tw:text-foreground">
            Change avatar
          </DialogTitle>
          <DialogDescription className="tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground">
            Upload an image or use your Gravatar.
          </DialogDescription>
        </DialogHeader>

        <div className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:pb-4">
          {state === "file-selected" && file ? (
            <div className="tw:flex tw:flex-col tw:gap-4 tw:overflow-hidden tw:rounded-lg tw:bg-muted tw:p-3">
              <div className="tw:flex tw:w-full tw:items-start tw:justify-between">
                <Avatar className="tw:size-10 tw:after:hidden">
                  <AvatarImage src={URL.createObjectURL(file)} alt="" />
                  <AvatarFallback>?</AvatarFallback>
                </Avatar>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(MD_BUTTON, "tw:font-medium")}
                  onClick={reset}
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="tw:flex tw:flex-col tw:gap-4 tw:overflow-hidden tw:rounded-lg tw:bg-muted tw:p-3">
              <div className="tw:flex tw:w-full tw:items-start tw:justify-between">
                <Avatar className="tw:size-10 tw:after:hidden">
                  <AvatarImage src={avatar} alt="" />
                  <AvatarFallback className="tw:bg-muted tw:text-sm tw:font-medium tw:text-muted-foreground">
                    YOU
                  </AvatarFallback>
                </Avatar>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(MD_BUTTON, "tw:font-medium")}
                  onClick={() => {
                    resetAvatar()
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          )}

          <label
            htmlFor="avatar-upload"
            onDragEnter={(e) => {
              e.preventDefault()
              setState("drag-over")
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              if (state === "drag-over") setState("default")
            }}
            onDragOver={(e) => {
              e.preventDefault()
              if (state !== "drag-over") setState("drag-over")
            }}
            onDrop={(e) => {
              e.preventDefault()
              handleFiles(e.dataTransfer.files)
            }}
            className={cn(
              "tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:overflow-hidden tw:rounded-sm tw:border tw:border-dashed tw:border-border tw:bg-muted tw:p-8",
              state === "drag-over" && "tw:border-primary tw:bg-primary/5"
            )}
          >
            <Upload
              className="tw:size-6 tw:shrink-0 tw:text-foreground"
              aria-hidden
            />
            <p className="tw:text-center tw:text-xs tw:font-semibold tw:leading-none">
              <span className="tw:text-primary">Click to upload</span>
              <span className="tw:text-foreground"> or drag and drop</span>
            </p>
            <p className="tw:text-center tw:text-xs tw:font-semibold tw:leading-none tw:text-muted-foreground">
              PNG or JPG, 2MB max
            </p>
            <input
              ref={inputRef}
              id="avatar-upload"
              type="file"
              accept="image/png,image/jpeg"
              className="tw:hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>

          <div className="tw:flex tw:items-center tw:justify-center tw:py-3">
            <p className="tw:flex-1 tw:truncate tw:text-center tw:text-xs tw:font-semibold tw:leading-none">
              <span className="tw:text-muted-foreground">Or</span>
              <a
                href="https://gravatar.com"
                target="_blank"
                rel="noreferrer"
                className="tw:text-primary"
              >
                {" "}
                edit your Gravatar
              </a>
            </p>
          </div>
        </div>

        <DialogFooter className="tw:mx-0 tw:flex tw:items-center tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-muted tw:p-4">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(MD_BUTTON, "tw:font-medium")}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={state !== "file-selected"}
            className={cn(MD_BUTTON, "tw:font-medium tw:disabled:opacity-50")}
            onClick={saveChanges}
          >
            Save changes
          </Button>
        </DialogFooter>

        <DialogClose className="tw:absolute tw:right-[15px] tw:top-[15px] tw:size-4 tw:rounded-xs tw:opacity-70 tw:hover:opacity-100">
          <X className="tw:size-4" aria-hidden />
          <span className="tw:sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}
